import { useEffect, useRef } from 'react'

import useReduxSelector from '../hooks/useReduxSelector'
import { identifyUser } from './identifyUser'

// Distinguishes "not yet identified" from an anonymous (undefined) user id.
const NOT_IDENTIFIED = Symbol('notIdentified')

// Identify once the feature flags fetch has settled (success or error), then
// again if the user id changes (e.g. signing in without a full reload).
export const useIdentifyUserOnSettle = (): void => {
  const userId = useReduxSelector((state) => state.auth.currentUser?.id)
  const authFetched = useReduxSelector((state) => state.auth.fetched)
  const featureFlags = useReduxSelector((state) => state.featureFlags.data)
  // Not `data != null`: the SSR seed fills that with the global flags before
  // the fetch, and identifying off it drops the viewer's own flags.
  const featureFlagsFetched = useReduxSelector(
    (state) => state.featureFlags.fetched,
  )
  const featureFlagsError = useReduxSelector(
    (state) => state.featureFlags.error,
  )
  const identifiedAs = useRef<typeof NOT_IDENTIFIED | number | undefined>(
    NOT_IDENTIFIED,
  )

  useEffect(() => {
    const featuresSettled = featureFlagsFetched || featureFlagsError != null
    if (!authFetched || !featuresSettled || identifiedAs.current === userId) {
      return
    }
    identifiedAs.current = userId
    identifyUser(userId, featureFlags)
  }, [
    authFetched,
    featureFlags,
    featureFlagsError,
    featureFlagsFetched,
    userId,
  ])
}
